import { useState, useEffect, useRef, useMemo } from 'react'
import styled from 'styled-components'
import { supabase } from '../../supabase'
import { ecrire, lire } from './erreurs'
import {
  dessinerTicket, fichiersTicket, enregistrerTickets, netDe, dateCourte,
  chargerPolice, policePrete,
} from './ticketPng'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  Btn, Input, Label, Field, Row, Cluster, ChipBar, Chip, Pill,
  TableWrap, Tableau, Loading, Empty, Muted, IconBtn, Select, useUI,
} from './ui'

const PRODUITS = ['paille', 'foin', 'luzerne']

/* Le ticket ne varie que par ces champs : le reste du modèle est figé
   sur la bascule (plaque 3, véhicule 3, balance A). */
const PLAQUE = '3'
const VEHICULE = '3'
const BALANCE = 'A'

const deux = (n) => String(n).padStart(2, '0')
/* `toISOString` bascule sur la veille en soirée l'été : la date du
   ticket se lit sur l'horloge locale, pas sur UTC. */
const dateLocale = (d = new Date()) =>
  `${d.getFullYear()}-${deux(d.getMonth() + 1)}-${deux(d.getDate())}`
const heureLocale = (d = new Date()) => `${deux(d.getHours())}:${deux(d.getMinutes())}`

const vide = () => ({
  compteur: '', date_pesee: dateLocale(), heure: heureLocale(),
  produit: '', client: '', fournisseur: '',
  poids1: '', poids2: '', nb_tickets: 1,
})

const entier = (v) => {
  if (v === '' || v == null) return null
  const n = parseInt(String(v).replace(/[^\d-]/g, ''), 10)
  return Number.isFinite(n) ? n : null
}

const Split = styled.div`
  display: grid; grid-template-columns: 1fr 400px;
  gap: 20px; align-items: start;
  @media (max-width: 1040px) { grid-template-columns: 1fr; }
`
const Colonne = styled.div`display: flex; flex-direction: column; gap: 16px;`

/* Le NET est le seul chiffre que Franck vérifie avant d'imprimer :
   il occupe la place que ça mérite. */
const Net = styled.div`
  display: flex; align-items: baseline; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
  padding: 13px 16px; margin: 2px 0 16px;
  border: 1px solid ${(p) => (p.$pret ? '#CFE2DC' : t.line)};
  background: ${(p) => (p.$pret ? t.goldWash : t.surfaceSunk)};
  border-radius: ${t.rSm};
`
const NetLabel = styled.span`
  font-family: ${t.font}; font-size: 0.635rem; font-weight: 600;
  letter-spacing: 0.075em; text-transform: uppercase;
  color: ${(p) => (p.$pret ? t.goldDeep : t.textFaint)};
`
const NetValeur = styled.span`
  font-family: ${t.mono}; font-size: 1.5rem; font-weight: 700;
  letter-spacing: -0.03em; font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$pret ? t.gold : t.textFaint)};
  span { font-size: 0.9rem; font-weight: 600; margin-left: 2px; }
`

/* L'aperçu montre exactement le PNG produit, à sa taille d'impression :
   ce qui se lit à l'écran est ce qui sortira de l'imprimante. */
const Apercu = styled.div`
  display: flex; justify-content: center;
  padding: 18px; background: ${t.surfaceSunk};
  border: 1px solid ${t.line}; border-radius: ${t.rSm};
  canvas {
    background: #fff; max-width: 100%;
    box-shadow: 0 1px 2px rgba(22,24,28,0.06), 0 6px 18px rgba(22,24,28,0.08);
  }
  @media (max-width: 460px) { padding: 9px; }
`
const Collant = styled.div`
  position: sticky; top: 16px;
  @media (max-width: 1040px) { position: static; }
`
const Aide = styled.p`
  font-family: ${t.font}; font-size: 0.715rem; line-height: 1.45;
  color: ${t.textFaint}; margin: 6px 0 0;
`
const Actions = styled.div`
  display: flex; flex-direction: column; gap: 8px; margin-top: 14px;
`

export default function TicketsAdmin() {
  const { toast, confirm } = useUI()
  const [form, setForm] = useState(vide)
  const [liste, setListe] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [echelle, setEchelle] = useState(2)
  /* Redessine l'aperçu dès que la police du ticket est disponible :
     sans ça, le premier rendu resterait sur le repli. */
  const [police, setPolice] = useState(policePrete)
  const canvasRef = useRef(null)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const net = useMemo(() => netDe(form.poids1, form.poids2), [form.poids1, form.poids2])
  const pret = net != null && net > 0

  /* Le ticket tel qu'il sera imprimé — sert à l'aperçu, aux PNG et,
     aux types près, à la ligne insérée en base. */
  const ticket = useMemo(() => ({
    compteur: entier(form.compteur),
    date_pesee: form.date_pesee,
    heure: form.heure,
    plaque: PLAQUE, vehicule: VEHICULE, balance: BALANCE,
    client: form.client.trim(),
    fournisseur: form.fournisseur.trim(),
    produit: form.produit,
    poids1: entier(form.poids1),
    poids2: entier(form.poids2),
    nb_tickets: form.nb_tickets,
  }), [form])

  useEffect(() => { charger() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let vivant = true
    chargerPolice().then((ok) => { if (vivant) setPolice(ok) })
    return () => { vivant = false }
  }, [])

  useEffect(() => {
    if (canvasRef.current) dessinerTicket(canvasRef.current, ticket, echelle)
  }, [ticket, echelle, police])

  const charger = async () => {
    setLoading(true)
    const data = await lire(
      supabase.from('tickets_pesee').select('*')
        .order('date_pesee', { ascending: false })
        .order('heure', { ascending: false })
        .limit(200),
      toast, 'chargement des tickets',
    )
    if (data) {
      setListe(data)
      /* Le compteur suit celui de la bascule : on propose le suivant
         sans jamais l'imposer. */
      setForm((f) => {
        if (f.compteur !== '') return f
        const max = data.reduce((m, x) => Math.max(m, x.compteur ?? 0), 0)
        return max ? { ...f, compteur: max + 1 } : f
      })
    }
    setLoading(false)
  }

  const nouveau = () => {
    const max = liste.reduce((m, x) => Math.max(m, x.compteur ?? 0), 0)
    setForm({ ...vide(), compteur: max ? max + 1 : '' })
  }

  /* Un seul geste : les PNG partent vers la galerie photo et la pesée
     est archivée. Les fichiers sont fabriqués de façon synchrone pour
     que la feuille de partage iOS reste rattachée au clic. */
  const enregistrer = async () => {
    if (!pret) return toast('Renseignez les deux poids', 'error')
    if (!form.heure) return toast("L'heure est requise", 'error')
    /* Attendre ici casserait le lien avec le clic, et iOS refuserait
       alors le partage : mieux vaut demander de réessayer que d'imprimer
       un ticket dans la mauvaise police sans le signaler. */
    if (!policePrete()) {
      chargerPolice()
      return toast('Police du ticket en cours de chargement — réessayez', 'error')
    }

    let mode
    try {
      mode = await enregistrerTickets(fichiersTicket(ticket, echelle))
    } catch (e) {
      console.error('génération du ticket', e)
      return toast(`Échec de la génération du ticket : ${e?.message || e}`, 'error')
    }
    if (mode === 'annule') return toast('Partage annulé — rien n’a été enregistré', 'error')

    setSaving(true)
    const ok = await ecrire(
      supabase.from('tickets_pesee').insert([{
        ...ticket,
        client: ticket.client || null,
        fournisseur: ticket.fournisseur || null,
        produit: ticket.produit || null,
      }]),
      toast, 'enregistrement du ticket',
    )
    setSaving(false)
    if (!ok) return
    toast(mode === 'partage'
      ? `Ticket ${form.nb_tickets === 2 ? '×2 ' : ''}prêt à enregistrer dans Photos`
      : `Ticket ${form.nb_tickets === 2 ? '×2 ' : ''}téléchargé`)
    nouveau()
    charger()
  }

  /* Réimpression : le PNG est régénéré depuis la ligne archivée. */
  const reimprimer = async (tk) => {
    try {
      await enregistrerTickets(fichiersTicket(tk, echelle))
    } catch (e) {
      console.error('réimpression du ticket', e)
      toast(`Échec de la réimpression : ${e?.message || e}`, 'error')
    }
  }

  const reprendre = (tk) => {
    setForm({
      compteur: tk.compteur ?? '',
      date_pesee: tk.date_pesee,
      heure: tk.heure,
      produit: tk.produit || '',
      client: tk.client || '',
      fournisseur: tk.fournisseur || '',
      poids1: tk.poids1 ?? '',
      poids2: tk.poids2 ?? '',
      nb_tickets: tk.nb_tickets || 1,
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const supprimer = async (tk) => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Supprimer ce ticket ?',
      body: <>La pesée n° <strong>{tk.compteur ?? '—'}</strong> du {dateCourte(tk.date_pesee)} ne
        pourra plus être réimprimée depuis l’admin.</>,
      confirmLabel: 'Supprimer',
    })
    if (!ok) return
    if (!await ecrire(
      supabase.from('tickets_pesee').delete().eq('id', tk.id),
      toast, 'suppression du ticket',
    )) return
    toast('Ticket supprimé')
    charger()
  }

  const totalJour = useMemo(() => {
    const auj = dateLocale()
    return liste.filter((x) => x.date_pesee === auj).reduce((s, x) => s + (x.net || 0), 0)
  }, [liste])

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Tickets de pesée</PageTitle>
          <PageSub>
            {liste.length} ticket{liste.length > 1 ? 's' : ''} archivé{liste.length > 1 ? 's' : ''}
            {totalJour > 0 && ` · ${(totalJour / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} T pesées aujourd’hui`}
          </PageSub>
        </div>
        <Cluster>
          <Btn $variant="ghost" $size="sm" onClick={nouveau}>Vider le formulaire</Btn>
        </Cluster>
      </PageHead>

      <Split>
        <Colonne>
          <Panel>
            <PanelHead>
              <PanelTitle>Nouvelle pesée</PanelTitle>
            </PanelHead>

            <Row $cols="1fr 1fr">
              <Field>
                <Label>1<sup>re</sup> pesée (kg)</Label>
                <Input
                  type="number" inputMode="numeric" placeholder="15400"
                  value={form.poids1} onChange={set('poids1')}
                />
              </Field>
              <Field>
                <Label>2<sup>e</sup> pesée (kg)</Label>
                <Input
                  type="number" inputMode="numeric" placeholder="37740"
                  value={form.poids2} onChange={set('poids2')}
                />
              </Field>
            </Row>

            <Net $pret={pret}>
              <NetLabel $pret={pret}>Net</NetLabel>
              <NetValeur $pret={pret}>
                {net != null ? net.toLocaleString('fr-FR') : '—'}<span>kg</span>
              </NetValeur>
            </Net>

            <Row $cols="1fr 1fr">
              <Field>
                <Label>Date</Label>
                <Input type="date" value={form.date_pesee} onChange={set('date_pesee')} />
              </Field>
              <Field>
                <Label>Heure</Label>
                <Input type="time" value={form.heure} onChange={set('heure')} />
              </Field>
            </Row>

            <Field>
              <Label>Produit</Label>
              <ChipBar>
                {PRODUITS.map((p) => (
                  <Chip
                    key={p} type="button" $on={form.produit === p}
                    onClick={() => setForm((f) => ({ ...f, produit: f.produit === p ? '' : p }))}
                  >
                    {p}
                  </Chip>
                ))}
              </ChipBar>
            </Field>

            <Row $cols="1fr 1fr">
              <Field>
                <Label>Client</Label>
                <Input placeholder="—" value={form.client} onChange={set('client')} />
              </Field>
              <Field>
                <Label>Fournisseur</Label>
                <Input placeholder="—" value={form.fournisseur} onChange={set('fournisseur')} />
              </Field>
            </Row>

            <Row $cols="1fr 1fr">
              <Field>
                <Label>Compteur</Label>
                <Input
                  type="number" inputMode="numeric"
                  placeholder={liste.length ? 'auto' : 'à amorcer'}
                  value={form.compteur} onChange={set('compteur')}
                />
                {/* Sans ticket archivé, il n'y a pas de « dernier » d'où
                    repartir : le numéro doit venir de la bascule une
                    première fois, sinon le ticket sort sans compteur. */}
                {!liste.length && (
                  <Aide>Premier ticket : reprenez le numéro affiché par la bascule.
                    Les suivants s’incrémenteront tout seuls.</Aide>
                )}
              </Field>
              <Field>
                <Label>Exemplaires</Label>
                <ChipBar>
                  <Chip
                    type="button" $on={form.nb_tickets === 1}
                    onClick={() => setForm((f) => ({ ...f, nb_tickets: 1 }))}
                  >
                    1 ticket
                  </Chip>
                  <Chip
                    type="button" $on={form.nb_tickets === 2}
                    onClick={() => setForm((f) => ({ ...f, nb_tickets: 2 }))}
                  >
                    2 tickets
                  </Chip>
                </ChipBar>
              </Field>
            </Row>
            <Muted>
              Deux tickets pour un camion et sa remorque imprimés séparément :
              même pesée, deux exemplaires.
            </Muted>
          </Panel>
        </Colonne>

        <Collant>
          <Panel $pad="16px">
            <PanelHead>
              <PanelTitle>Aperçu</PanelTitle>
              <Pill $tone="neutral">58 mm · 384 pts</Pill>
            </PanelHead>
            <Apercu>
              <canvas ref={canvasRef} />
            </Apercu>

            <Actions>
              <Btn $block $size="lg" onClick={enregistrer} disabled={!pret || saving}>
                {saving ? 'Enregistrement…' : 'Enregistrer dans Photos'}
              </Btn>
              <Muted style={{ margin: 0, textAlign: 'center', fontSize: '0.72rem' }}>
                Sur iPhone : « Enregistrer {form.nb_tickets === 2 ? '2 images' : "l'image"} »
                dans la feuille de partage, puis direct dans l’appli de l’imprimante.
              </Muted>
            </Actions>

            <Field style={{ marginTop: 16, marginBottom: 0 }}>
              <Label>Définition du PNG</Label>
              <Select value={echelle} onChange={(e) => setEchelle(Number(e.target.value))}>
                <option value={1}>×1 — 384 px, un point imprimé par pixel</option>
                <option value={2}>×2 — 768 px, recommandé</option>
                <option value={3}>×3 — 1152 px, image plus lourde</option>
              </Select>
            </Field>
            <Muted style={{ fontSize: '0.72rem' }}>
              Si l’impression sort floue ou tronquée, essayez ×1 : l’image
              correspond alors exactement à la tête d’impression.
            </Muted>
          </Panel>
        </Collant>
      </Split>

      <Panel style={{ marginTop: 20 }}>
        <PanelHead>
          <PanelTitle>Tickets émis</PanelTitle>
        </PanelHead>

        {loading ? <Loading rows={4} /> : liste.length === 0 ? (
          <Empty icon="🧾" title="Aucun ticket">
            Les pesées enregistrées apparaîtront ici, réimprimables à tout moment.
          </Empty>
        ) : (
          <TableWrap>
            <Tableau $min="820px">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Heure</th>
                  <th className="num">N°</th>
                  <th>Produit</th>
                  <th>Client</th>
                  <th className="num">1<sup>re</sup></th>
                  <th className="num">2<sup>e</sup></th>
                  <th className="num">Net</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {liste.map((tk, i) => (
                  <Stagger key={tk.id} as="tr" $i={i}>
                    <td className="fort">{dateCourte(tk.date_pesee)}</td>
                    <td>{tk.heure}</td>
                    <td className="num">{tk.compteur ?? '—'}</td>
                    <td>
                      {tk.produit ? <Pill $tone="gold">{tk.produit}</Pill> : '—'}
                      {tk.nb_tickets === 2 && <Pill $tone="neutral" style={{ marginLeft: 5 }}>×2</Pill>}
                    </td>
                    <td>{tk.client || '—'}</td>
                    <td className="num">{tk.poids1?.toLocaleString('fr-FR')}</td>
                    <td className="num">{tk.poids2?.toLocaleString('fr-FR')}</td>
                    <td className="num fort">{tk.net?.toLocaleString('fr-FR')} kg</td>
                    <td>
                      <Cluster $gap="4px">
                        <IconBtn title="Réimprimer" onClick={() => reimprimer(tk)}>🖨️</IconBtn>
                        <IconBtn title="Reprendre dans le formulaire" onClick={() => reprendre(tk)}>✏️</IconBtn>
                        <IconBtn title="Supprimer" onClick={() => supprimer(tk)}>🗑️</IconBtn>
                      </Cluster>
                    </td>
                  </Stagger>
                ))}
              </tbody>
            </Tableau>
          </TableWrap>
        )}
      </Panel>
    </>
  )
}
